import { Option, pipe, ReadonlyArray, Record } from "fp";

import React from "react";

import * as Log from "~/Log";

export namespace Issues {
  export const column = (settings: Settings) => ({
    title: "Issues",
    dataIndex: "issues",
    defaultFilteredValue: settings.issues,
    ...filters(),
    onFilter,
    sorter,
    render,
  });

  const filters = () => ({
    filters: Log.Summary.Issues.list().map((value) => ({
      text: value,
      value,
    })),
  });

  const onFilter = (
    value: string | number | boolean,
    { summary: { issues } }: Log.Summary
  ) => {
    switch (value) {
      case Log.Summary.Issues.violationBreak():
        return issues.violations.break;
      case Log.Summary.Issues.violationCycle():
        return issues.violations.cycle;
      case Log.Summary.Issues.violationDrive():
        return issues.violations.drive;
      case Log.Summary.Issues.violationShift():
        return issues.violations.shift;
      case Log.Summary.Issues.notSigned():
        return issues.notSigned;
      case Log.Summary.Issues.missingDVIR():
        return issues.missingDVIR
      default:
        return false;
    }
  };

  const sorter = (a: Log.Summary, b: Log.Summary) =>
    Log.Summary.Issues.compare(a.summary, b.summary);

  const render = (_: unknown, { summary }: Log.Summary) => (
    <Log.Summary.Issues {...summary} />
  );

  export type Settings = {
    readonly issues: readonly ReturnType<
      typeof Log.Summary.Issues.list
    >[number][];
  };

  export namespace Settings {
    export const isEmpty = ({ issues }: Settings): boolean =>
      issues.length == 0;

    export const encode = ({ issues }: Partial<Settings>): readonly string[] =>
      pipe(
        issues ?? [],
        ReadonlyArray.map((issue) => parameterNames()[issue])
      );

    export const parse = (
      getParameter: Log.Summaries.Table.Settings.GetParameter
    ): Settings =>
      pipe(
        parameterNames(),
        Record.toArray,
        ReadonlyArray.filterMap(([issue, parameterName]) =>
          pipe(
            getParameter(parameterName),
            Option.chain(shouldBeApplied),
            Option.map(() => issue)
          )
        ),
        (issues) => ({ issues })
      );

    const shouldBeApplied = Option.fromPredicate(
      (parameterValue) => parameterValue === "true" || parameterValue === ""
    );

    const parameterNames = () =>
      ({
        [Log.Summary.Issues.violationBreak()]: "break-violation",
        [Log.Summary.Issues.violationCycle()]: "cycle-violation",
        [Log.Summary.Issues.violationDrive()]: "drive-violation",
        [Log.Summary.Issues.violationShift()]: "shift-violation",
        [Log.Summary.Issues.notSigned()]: "not-signed",
        [Log.Summary.Issues.missingDVIR()]: "missing-dvir",
      } as const);
  }
}
